import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { useAuth } from "../../contexts/AuthContext";
import {
  getDayPost,
  generateDateID,
  calculateAndSavePeakScore,
} from "../../services/dayPostService";
import { userService } from "../../services/userService";
import { DayPost, PeakScoreLog } from "../../types/post";
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  XMarkIcon,
  CalendarIcon,
  LockClosedIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import AIInsightCard from "../../components/common/AIInsightCard";
import Header from "../../components/layout/Header";
import { SparklesIcon } from "@heroicons/react/24/solid";
import ComingSoon from "../../components/ComingSoon";
import { useSubscription } from "../../providers/SubscriptionProvider";
import { useThemeStore } from "../../stores/themeStore";
import {
  averageRecordedWorkload,
  recordedWorkloadFactor,
} from "../../utils/loadBalanceDisplay";

interface PeakData {
  date: string;
  dateLabel: string;
  score: number;
  hasData: boolean;
  wellnessFinal?: number;
  hlr?: number;
  workloadFactor?: number;
  sickFactor?: number;
  sick?: boolean;
  injuryPenalty?: number;
  wellnessA?: number;
  trainingLoad?: number;
  niggle?: boolean;
  [key: string]: any; // Index signature for Nivo compatibility
}

// Custom hook for responsive screen size detection
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    isMobile: typeof window !== "undefined" ? window.innerWidth < 768 : false,
    isTablet:
      typeof window !== "undefined"
        ? window.innerWidth >= 768 && window.innerWidth < 1024
        : false,
    isDesktop: typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
};

const PeakInsights: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { width: screenWidth, isMobile, isTablet, isDesktop } = useScreenSize();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "peakx-dark";
  const nivoTheme = {
    axis: {
      ticks: {
        text: {
          fill: isDark ? "#e5e5e5" : "#374151",
        },
      },
    },
    grid: {
      line: {
        stroke: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        strokeWidth: 1,
      },
    },
  };

  const [peakData, setPeakData] = useState<PeakData[]>([]);
  const [monthlyData, setMonthlyData] = useState<PeakData[]>([]);
  const [quarterlyData, setQuarterlyData] = useState<PeakData[]>([]);
  const [currentDayPeak, setCurrentDayPeak] = useState<PeakScoreLog | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<
    "Day" | "Week" | "Month" | "Quarter" | "Custom"
  >("Day");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (dropdownOpen && !target.closest(".recalculate-dropdown")) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Close metric dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        metricDropdownRef.current &&
        !metricDropdownRef.current.contains(event.target as Node)
      ) {
        setShowMetricDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // State for Month view display mode (daily vs weekly)
  const [monthViewMode, setMonthViewMode] = useState<"daily" | "weekly">(
    "daily",
  );

  const [recalculating, setRecalculating] = useState(false);
  const [bulkRecalculating, setBulkRecalculating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [dateResults, setDateResults] = useState<
    Array<{
      dateId: string;
      dateLabel: string;
      status: "pending" | "processing" | "success" | "error";
      peakScore?: number;
      error?: string;
    }>
  >([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const [recalcError, setRecalcError] = useState<string | null>(null);
  const [bulkRecalcError, setBulkRecalcError] = useState<string | null>(null);

  // Chart options state
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
    new Set([
      "peakScore",
      "wellnessFinal",
      "hlr",
      "workloadFactor",
      "trainingLoad",
    ]),
  );
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);
  const metricDropdownRef = useRef<HTMLDivElement>(null);
  const [hoveredSlice, setHoveredSlice] = useState<any>(null);
  const [pinnedSlice, setPinnedSlice] = useState<any>(null);

  // Custom date range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [hasCustomDateAccess, setHasCustomDateAccess] =
    useState<boolean>(false);
  const [customRangeData, setCustomRangeData] = useState<PeakData[]>([]);
  const [customRangeLoading, setCustomRangeLoading] = useState(false);

  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const {
    subscription: userSubscription,
    hasSubscription,
    isPremium,
    isPlusOrHigher,
    refreshSubscription,
  } = useSubscription();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // Only Premium/Platinum/Coach get access to advanced graph features
  const hasQuarterlyAccess = isPremium;
  const hasMonthlyAccess = isPlusOrHigher;

  // Available metrics for plotting - Peak Score, HLR, Wellness, Training Load, and Load Balance
  const availableMetrics = [
    { key: "peakScore", label: "Peak Score", color: "#3b82f6" },
    { key: "wellnessFinal", label: "Wellness", color: "#10b981" },
    { key: "hlr", label: "HLR", color: "#8b5cf6" },
    {
      key: "workloadFactor",
      label: "Load Balance",
      color: isDark ? "#ffffff" : "#374151",
    },
    { key: "trainingLoad", label: "Training Load", color: "#9ca3af" },
  ];

  // Only Premium/Platinum/Coach get advanced graph features
  useEffect(() => {
    setHasCustomDateAccess(isPremium);
  }, [isPremium]);

  // Get date from URL parameter, default to today
  const dateParam = searchParams.get("date");
  const targetDate = useMemo(() => {
    if (dateParam && /^\d{8}$/.test(dateParam)) {
      const year = parseInt(dateParam.substring(0, 4));
      const month = parseInt(dateParam.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateParam.substring(6, 8));
      return new Date(year, month, day);
    }
    return new Date();
  }, [dateParam]);

  // Generate past 7 days including the target date
  const generatePastWeekDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate past 28 days including the target date
  const generatePast28Days = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 27; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate past 12 weeks of dates (weekly averages)
  const generatePast12Weeks = (endDate: Date): Date[][] => {
    const weeks: Date[][] = [];
    for (let weekIndex = 0; weekIndex < 12; weekIndex++) {
      const weekDates: Date[] = [];
      const weekStartDate = new Date(endDate);
      weekStartDate.setDate(endDate.getDate() - weekIndex * 7 - 6);

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const date = new Date(weekStartDate);
        date.setDate(weekStartDate.getDate() + dayIndex);
        weekDates.push(date);
      }
      weeks.push(weekDates);
    }
    return weeks;
  };

  // Calculate peak score from logs
  const calculatePeakScore = (peakScoreLog?: PeakScoreLog): number => {
    return peakScoreLog?.peakScore || 0;
  };

  // Whether the user actually checked "unwell" for this specific day's wellness log.
  // Must NOT be derived from peakScoreLog.sickFactor, which is a multi-day recovery
  // decay value and stays < 1 for several days after a sick entry even once the box
  // is no longer checked.
  const getDaySick = (dayPost?: DayPost | null): boolean => {
    if (!dayPost?.wellnessLogs || dayPost.wellnessLogs.length === 0)
      return false;
    return dayPost.wellnessLogs[dayPost.wellnessLogs.length - 1].sick ?? false;
  };

  // Format date for display
  const formatDateLabel = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  };

  // Fetch all peak data when component mounts or date changes
  useEffect(() => {
    const fetchPeakData = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch current day's detailed peak data
        const dateID = generateDateID(targetDate);
        const dayPost = await getDayPost(currentUser.uid, dateID);
        if (dayPost && dayPost.peakScoreLog) {
          setCurrentDayPeak(dayPost.peakScoreLog);
        } else {
          setCurrentDayPeak(null);
        }

        // Fetch week data
        const weekDates = generatePastWeekDates(targetDate);
        const peakPromises = weekDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = dayPost
              ? calculatePeakScore(dayPost.peakScoreLog)
              : 0;

            // Calculate training load for the day
            const trainingLoad = dayPost?.trainingLogs
              ? dayPost.trainingLogs.reduce(
                  (sum, log) => sum + (log.load || 0),
                  0,
                )
              : 0;

            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score,
              hasData: dayPost !== null && dayPost.peakScoreLog !== undefined,
              wellnessFinal: dayPost?.peakScoreLog?.wellnessFinal || 0,
              hlr: dayPost?.peakScoreLog?.hlr || 0,
              workloadFactor: recordedWorkloadFactor(dayPost?.peakScoreLog),
              sickFactor: dayPost?.peakScoreLog?.sickFactor || 0,
              sick: getDaySick(dayPost),
              injuryPenalty: dayPost?.peakScoreLog?.injuryPenalty || 0,
              wellnessA: dayPost?.peakScoreLog?.wellnessA || 0,
              trainingLoad,
              niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
            };
          } catch (error) {
            console.error(`Error fetching data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score: 0,
              hasData: false,
              wellnessFinal: 0,
              hlr: 0,
              workloadFactor: 0,
              sickFactor: 0,
              sick: false,
              injuryPenalty: 0,
              wellnessA: 0,
              trainingLoad: 0,
              niggle: false,
            };
          }
        });

        const weekResults = await Promise.all(peakPromises);
        setPeakData(weekResults);

        // Fetch month data only when Plus+ — free accounts must not receive 28-day charts.
        if (!hasMonthlyAccess) {
          setMonthlyData([]);
        } else {
          const monthDates = generatePast28Days(targetDate);
          const monthPromises = monthDates.map(async (date) => {
            const dateID = generateDateID(date);
            const dateLabel = formatDateLabel(date);

            try {
              const dayPost = await getDayPost(currentUser.uid, dateID);

              if (!dayPost || !dayPost.peakScoreLog) {
                return {
                  date: dateID,
                  dateLabel,
                  score: 0,
                  hasData: false,
                  wellnessFinal: 0,
                  hlr: 0,
                  workloadFactor: 0,
                  sickFactor: 0,
                  sick: false,
                  injuryPenalty: 0,
                  wellnessA: 0,
                  trainingLoad: 0,
                  niggle: false,
                };
              }

              const score = calculatePeakScore(dayPost.peakScoreLog);
              const wellnessFinal = dayPost.peakScoreLog.wellnessFinal || 0;
              const hlr = dayPost.peakScoreLog.hlr || 0;
              const workloadFactor = recordedWorkloadFactor(dayPost.peakScoreLog);
              const sickFactor = dayPost.peakScoreLog.sickFactor || 0;
              const injuryPenalty = dayPost.peakScoreLog.injuryPenalty || 0;
              const wellnessA = dayPost.peakScoreLog.wellnessA || 0;

              // Calculate training load for this day
              const trainingLoad = dayPost.trainingLogs
                ? dayPost.trainingLogs.reduce(
                    (sum, log) => sum + (log.load || 0),
                    0,
                  )
                : 0;

              return {
                date: dateID,
                dateLabel,
                score,
                hasData: score > 0 || wellnessFinal > 0 || trainingLoad > 0,
                wellnessFinal,
                hlr,
                workloadFactor,
                sickFactor,
                sick: getDaySick(dayPost),
                injuryPenalty,
                wellnessA,
                trainingLoad,
                niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
              };
            } catch (error) {
              console.error(`Error fetching data for ${dateID}:`, error);
              return {
                date: dateID,
                dateLabel,
                score: 0,
                hasData: false,
                wellnessFinal: 0,
                hlr: 0,
                workloadFactor: 0,
                sickFactor: 0,
                sick: false,
                injuryPenalty: 0,
                wellnessA: 0,
                trainingLoad: 0,
                niggle: false,
              };
            }
          });

          const monthResults = await Promise.all(monthPromises);
          setMonthlyData(monthResults);
        }

        // Fetch quarterly data (12 weeks of weekly averages)
        if (hasQuarterlyAccess) {
          const quarterWeeks = generatePast12Weeks(targetDate);
          const quarterPromises = quarterWeeks.map(
            async (weekDates, weekIndex) => {
              // Get the week start date (first day of the week)
              const weekStartDate = weekDates[0];
              const weekLabel = weekStartDate.toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
              });

              // Fetch data for all days in this week
              const weekDataPromises = weekDates.map(async (date) => {
                const dateID = generateDateID(date);
                try {
                  const dayPost = await getDayPost(currentUser.uid, dateID);
                  return dayPost;
                } catch (error) {
                  console.error(`Error fetching data for ${dateID}:`, error);
                  return null;
                }
              });

              const weekData = await Promise.all(weekDataPromises);
              const validDays = weekData.filter(
                (day): day is NonNullable<typeof day> =>
                  day !== null &&
                  day !== undefined &&
                  day.peakScoreLog !== undefined,
              );

              if (validDays.length === 0) {
                return {
                  date: weekLabel,
                  dateLabel: weekLabel,
                  score: 0,
                  hasData: false,
                  wellnessFinal: 0,
                  hlr: 0,
                  workloadFactor: 0,
                  sickFactor: 0,
                  sick: false,
                  injuryPenalty: 0,
                  wellnessA: 0,
                  trainingLoad: 0,
                  niggle: false,
                };
              }

              // Calculate weekly averages
              const avgScore =
                validDays.reduce(
                  (sum, day) => sum + calculatePeakScore(day.peakScoreLog),
                  0,
                ) / validDays.length;
              const avgWellness =
                validDays.reduce(
                  (sum, day) => sum + (day.peakScoreLog?.wellnessFinal || 0),
                  0,
                ) / validDays.length;
              const avgHlr =
                validDays.reduce(
                  (sum, day) => sum + (day.peakScoreLog?.hlr || 0),
                  0,
                ) / validDays.length;
              const avgWorkload = averageRecordedWorkload(
                validDays.map((day) => day.peakScoreLog),
              );
              const avgSick =
                validDays.reduce(
                  (sum, day) => sum + (day.peakScoreLog?.sickFactor || 0),
                  0,
                ) / validDays.length;
              // Use min sickFactor so any sick day in the week flags it
              const minSick = validDays.reduce(
                (min, day) => Math.min(min, day.peakScoreLog?.sickFactor ?? 1),
                1,
              );
              // Use max injuryPenalty so any injured day in the week flags it
              const maxInjury = validDays.reduce(
                (max, day) =>
                  Math.max(max, day.peakScoreLog?.injuryPenalty ?? 0),
                0,
              );
              const avgWellnessA =
                validDays.reduce(
                  (sum, day) => sum + (day.peakScoreLog?.wellnessA || 0),
                  0,
                ) / validDays.length;

              // Calculate training load (average daily training load for the week)
              const totalTrainingLoad = validDays.reduce((sum, day) => {
                if (!day.trainingLogs) return sum;
                const dayLoad = day.trainingLogs.reduce(
                  (daySum, log) => daySum + (log.load || 0),
                  0,
                );
                return sum + dayLoad;
              }, 0);
              const trainingLoad = totalTrainingLoad / 7; // Convert to daily average
              // Check if any day in the week had niggles
              const hasNiggle = validDays.some(
                (day) => (day.niggleLogs?.length ?? 0) > 0,
              );
              // Flag the week as "sick" only if the box was actually checked that week
              const weekSick = validDays.some((day) => getDaySick(day));

              return {
                date: weekLabel,
                dateLabel: weekLabel,
                score: avgScore,
                hasData: true,
                wellnessFinal: avgWellness,
                hlr: avgHlr,
                workloadFactor: avgWorkload,
                sickFactor: minSick,
                sick: weekSick,
                injuryPenalty: maxInjury,
                wellnessA: avgWellnessA,
                trainingLoad: trainingLoad,
                niggle: hasNiggle,
              };
            },
          );

          const quarterResults = await Promise.all(quarterPromises);
          // Reverse the array so that the most recent week appears on the right
          setQuarterlyData(quarterResults.reverse());
        } else {
          setQuarterlyData([]);
        }
      } catch (err) {
        console.error("Error fetching peak data:", err);
        setError("Failed to load peak data");
      } finally {
        setLoading(false);
      }
    };

    fetchPeakData();
  }, [currentUser?.uid, targetDate, refreshToken, hasMonthlyAccess, hasQuarterlyAccess]);

  // Fetch custom date range data
  const fetchCustomRangeData = async (startDate: string, endDate: string) => {
    if (!startDate || !endDate || !currentUser?.uid) {
      return;
    }

    setCustomRangeLoading(true);
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Calculate the number of days in the range
      const daysDiff =
        Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) +
        1;

      // If more than 30 days, use weekly averages
      if (daysDiff > 30) {
        // Generate weeks for the custom range
        const weeks: Date[][] = [];
        let currentWeekStart = new Date(start);

        while (currentWeekStart <= end) {
          const weekDates: Date[] = [];
          for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(currentWeekStart.getDate() + i);
            if (date <= end) {
              weekDates.push(date);
            }
          }
          if (weekDates.length > 0) {
            weeks.push(weekDates);
          }
          currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }

        // Fetch weekly averages
        const weekPromises = weeks.map(async (weekDates) => {
          const weekStartDate = weekDates[0];
          const weekLabel = weekStartDate.toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
          });

          const weekDataPromises = weekDates.map(async (date) => {
            const dateID = generateDateID(date);
            try {
              const dayPost = await getDayPost(currentUser.uid, dateID);
              return dayPost;
            } catch (error) {
              return null;
            }
          });

          const weekData = await Promise.all(weekDataPromises);
          const validDays = weekData.filter(
            (day): day is NonNullable<typeof day> =>
              day !== null &&
              day !== undefined &&
              day.peakScoreLog !== undefined,
          );

          if (validDays.length === 0) {
            return {
              date: weekLabel,
              dateLabel: weekLabel,
              score: 0,
              hasData: false,
              wellnessFinal: 0,
              hlr: 0,
              workloadFactor: 0,
              sickFactor: 0,
              sick: false,
              injuryPenalty: 0,
              wellnessA: 0,
              trainingLoad: 0,
              niggle: false,
            };
          }

          // Calculate weekly averages
          const avgScore =
            validDays.reduce(
              (sum, day) => sum + calculatePeakScore(day.peakScoreLog),
              0,
            ) / validDays.length;
          const avgWellness =
            validDays.reduce(
              (sum, day) => sum + (day.peakScoreLog?.wellnessFinal || 0),
              0,
            ) / validDays.length;
          const avgHlr =
            validDays.reduce(
              (sum, day) => sum + (day.peakScoreLog?.hlr || 0),
              0,
            ) / validDays.length;
          const avgWorkload = averageRecordedWorkload(
            validDays.map((day) => day.peakScoreLog),
          );
          const avgSick =
            validDays.reduce(
              (sum, day) => sum + (day.peakScoreLog?.sickFactor || 0),
              0,
            ) / validDays.length;
          // Use min sickFactor so any sick day in the week flags it
          const minSick = validDays.reduce(
            (min, day) => Math.min(min, day.peakScoreLog?.sickFactor ?? 1),
            1,
          );
          // Use max injuryPenalty so any injured day in the week flags it
          const maxInjury = validDays.reduce(
            (max, day) => Math.max(max, day.peakScoreLog?.injuryPenalty ?? 0),
            0,
          );
          const avgWellnessA =
            validDays.reduce(
              (sum, day) => sum + (day.peakScoreLog?.wellnessA || 0),
              0,
            ) / validDays.length;

          // Calculate training load (average daily training load for the week)
          const totalTrainingLoad = validDays.reduce((sum, day) => {
            if (!day.trainingLogs) return sum;
            const dayLoad = day.trainingLogs.reduce(
              (daySum, log) => daySum + (log.load || 0),
              0,
            );
            return sum + dayLoad;
          }, 0);
          const trainingLoad = totalTrainingLoad / 7;
          const hasNiggle = validDays.some(
            (day) => (day.niggleLogs?.length ?? 0) > 0,
          );
          // Flag the week as "sick" only if the box was actually checked that week
          const weekSick = validDays.some((day) => getDaySick(day));

          return {
            date: weekLabel,
            dateLabel: weekLabel,
            score: avgScore,
            hasData: true,
            wellnessFinal: avgWellness,
            hlr: avgHlr,
            workloadFactor: avgWorkload,
            sickFactor: minSick,
            sick: weekSick,
            injuryPenalty: maxInjury,
            wellnessA: avgWellnessA,
            trainingLoad: trainingLoad,
            niggle: hasNiggle,
          };
        });

        const results = await Promise.all(weekPromises);
        setCustomRangeData(results);
      } else {
        // Use daily data for 30 days or less
        const dates: Date[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }

        const promises = dates.map(async (date) => {
          const dateID = generateDateID(date);
          const dateLabel = date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          });

          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);

            if (!dayPost || !dayPost.peakScoreLog) {
              return {
                date: dateID,
                dateLabel,
                score: 0,
                hasData: false,
                wellnessFinal: 0,
                hlr: 0,
                workloadFactor: 0,
                sickFactor: 0,
                sick: false,
                injuryPenalty: 0,
                wellnessA: 0,
                trainingLoad: 0,
                niggle: false,
              };
            }

            const score = calculatePeakScore(dayPost.peakScoreLog);
            const trainingLoad = dayPost.trainingLogs
              ? dayPost.trainingLogs.reduce(
                  (sum, log) => sum + (log.load || 0),
                  0,
                )
              : 0;

            return {
              date: dateID,
              dateLabel,
              score,
              hasData: true,
              wellnessFinal: dayPost.peakScoreLog.wellnessFinal || 0,
              hlr: dayPost.peakScoreLog.hlr || 0,
              workloadFactor: recordedWorkloadFactor(dayPost.peakScoreLog),
              sickFactor: dayPost.peakScoreLog.sickFactor || 0,
              sick: getDaySick(dayPost),
              injuryPenalty: dayPost.peakScoreLog.injuryPenalty || 0,
              wellnessA: dayPost.peakScoreLog.wellnessA || 0,
              trainingLoad,
              niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
            };
          } catch (error) {
            return {
              date: dateID,
              dateLabel,
              score: 0,
              hasData: false,
              wellnessFinal: 0,
              hlr: 0,
              workloadFactor: 0,
              sickFactor: 0,
              sick: false,
              injuryPenalty: 0,
              wellnessA: 0,
              trainingLoad: 0,
              niggle: false,
            };
          }
        });

        const results = await Promise.all(promises);
        setCustomRangeData(results);
      }
    } catch (error) {
      console.error("Error fetching custom range data:", error);
    } finally {
      setCustomRangeLoading(false);
    }
  };

  // Manually trigger recalculation and refresh
  const handleRecalculate = async () => {
    if (!currentUser?.uid) return;
    try {
      setRecalcError(null);
      setRecalculating(true);
      const dateID = generateDateID(targetDate);
      const result = await calculateAndSavePeakScore(dateID);
      if (!result?.success) {
        setRecalcError(result?.error || "Failed to recalculate");
      }
      // Trigger data refresh regardless to reflect any server-side updates
      setRefreshToken((v) => v + 1);
    } catch (e: any) {
      setRecalcError(e?.message || "Failed to recalculate");
    } finally {
      setRecalculating(false);
    }
  };

  // Bulk recalculation for past 90 days
  const handleBulkRecalculate = async () => {
    if (!currentUser?.uid) return;
    try {
      setBulkRecalcError(null);
      setBulkRecalculating(true);
      setBulkProgress(0);
      setShowProgressDialog(true);

      // Generate past 90 days of date IDs with labels
      const dates: Array<{ dateId: string; dateLabel: string; date: Date }> =
        [];
      for (let i = 0; i < 90; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateId = generateDateID(date);
        const dateLabel = date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        dates.push({ dateId, dateLabel, date });
      }

      // Initialize results state
      const initialResults = dates.map(({ dateId, dateLabel }) => ({
        dateId,
        dateLabel,
        status: "pending" as const,
      }));
      setDateResults(initialResults);

      let successCount = 0;
      let errorCount = 0;

      // Process dates in batches to avoid overwhelming the server
      const batchSize = 5;
      for (let i = 0; i < dates.length; i += batchSize) {
        const batch = dates.slice(i, i + batchSize);

        // Mark batch as processing
        setDateResults((prev) =>
          prev.map((result) => {
            const isInBatch = batch.some((b) => b.dateId === result.dateId);
            return isInBatch
              ? { ...result, status: "processing" as const }
              : result;
          }),
        );

        // Process batch in parallel
        // eslint-disable-next-line no-loop-func
        const batchPromises = batch.map(async ({ dateId }) => {
          try {
            const result = await calculateAndSavePeakScore(dateId);
            if (result?.success) {
              successCount++;
              // Update result with success
              setDateResults((prev) =>
                prev.map((r) =>
                  r.dateId === dateId
                    ? {
                        ...r,
                        status: "success" as const,
                        peakScore: result.data?.peakScore,
                      }
                    : r,
                ),
              );
            } else {
              errorCount++;
              console.warn(
                `Failed to recalculate for ${dateId}:`,
                result?.error,
              );
              // Update result with error
              setDateResults((prev) =>
                prev.map((r) =>
                  r.dateId === dateId
                    ? {
                        ...r,
                        status: "error" as const,
                        error: result?.error || "Unknown error",
                      }
                    : r,
                ),
              );
            }
          } catch (error) {
            errorCount++;
            console.error(`Error recalculating for ${dateId}:`, error);
            // Update result with error
            setDateResults((prev) =>
              prev.map((r) =>
                r.dateId === dateId
                  ? {
                      ...r,
                      status: "error" as const,
                      error:
                        error instanceof Error
                          ? error.message
                          : "Unknown error",
                    }
                  : r,
              ),
            );
          }
        });

        await Promise.all(batchPromises);

        // Update progress
        const processed = Math.min(i + batchSize, dates.length);
        setBulkProgress((processed / dates.length) * 100);

        // Small delay between batches to be gentle on the server
        if (i + batchSize < dates.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      if (errorCount > 0) {
        setBulkRecalcError(
          `Completed with ${errorCount} errors. ${successCount} days recalculated successfully.`,
        );
      }

      // Trigger data refresh
      setRefreshToken((v) => v + 1);
    } catch (e: any) {
      setBulkRecalcError(e?.message || "Failed to start bulk recalculation");
    } finally {
      setBulkRecalculating(false);
      setBulkProgress(0);
    }
  };

  // Calculate average score based on selected tab
  const averageScore = useMemo(() => {
    if (selectedTab === "Day") {
      return currentDayPeak?.peakScore || 0;
    } else if (selectedTab === "Week") {
      const scoresWithData = peakData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Month") {
      const scoresWithData = monthlyData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Quarter") {
      const scoresWithData = quarterlyData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Custom") {
      const scoresWithData = customRangeData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    }
    return 0;
  }, [
    selectedTab,
    currentDayPeak,
    peakData,
    monthlyData,
    quarterlyData,
    customRangeData,
  ]);

  // Calculate 7-day and 28-day averages for reference lines
  const averageLines = useMemo(() => {
    // For 7-day average calculation
    const weekData = selectedTab === "Week" ? peakData : monthlyData.slice(-7);
    const weekScoresWithData = weekData.filter((d) => d.hasData && d.score > 0);

    // For 28-day average calculation, use all monthly data (28 days available)
    const monthScoresWithData = monthlyData.filter(
      (d) => d.hasData && d.score > 0,
    );

    // Calculate 7-day average
    const sevenDayAvg =
      weekScoresWithData.length > 0
        ? weekScoresWithData.reduce((sum, d) => sum + d.score, 0) /
          weekScoresWithData.length
        : 0;

    // Calculate 28-day average from all available daily data
    const fourWeekAvg =
      monthScoresWithData.length > 0
        ? monthScoresWithData.reduce((sum, d) => sum + d.score, 0) /
          monthScoresWithData.length
        : 0;

    return {
      sevenDay: sevenDayAvg,
      fourWeek: fourWeekAvg,
    };
  }, [selectedTab, peakData, monthlyData]);

  // Group monthly data (28 days) into 4 weekly averages for the weekly view
  const monthlyWeeklyData = useMemo(() => {
    if (monthlyData.length === 0) return [];

    const weeklyData: PeakData[] = [];
    for (let week = 0; week < 4; week++) {
      const weekStart = week * 7;
      const weekEnd = weekStart + 7;
      const weekSlice = monthlyData.slice(weekStart, weekEnd);

      const validData = weekSlice.filter((d) => d.hasData);
      if (validData.length === 0) {
        weeklyData.push({
          date: weekSlice[0]?.date || "",
          dateLabel: `Week ${week + 1}`,
          score: 0,
          hasData: false,
        });
        continue;
      }

      const avg = (key: string) => {
        const vals = validData
          .filter((d) => d[key] !== undefined && d[key] > 0)
          .map((d) => d[key] as number);
        return vals.length > 0
          ? vals.reduce((a, b) => a + b, 0) / vals.length
          : 0;
      };

      weeklyData.push({
        date: weekSlice[0]?.date || "",
        dateLabel: `Week ${week + 1}`,
        score: Math.round(avg("score")),
        hasData: true,
        wellnessFinal: avg("wellnessFinal") || undefined,
        hlr: avg("hlr") || undefined,
        workloadFactor: avg("workloadFactor") || undefined,
        sickFactor: avg("sickFactor") || undefined,
        sick: weekSlice.some((d) => d.sick),
        injuryPenalty: avg("injuryPenalty") || undefined,
        wellnessA: avg("wellnessA") || undefined,
        trainingLoad: avg("trainingLoad") || undefined,
        niggle: weekSlice.some((d) => d.niggle),
      });
    }
    return weeklyData;
  }, [monthlyData]);

  // Get the correct month data based on the view mode
  const monthChartData = useMemo(() => {
    if (monthViewMode === "weekly") return monthlyWeeklyData;
    return monthlyData;
  }, [monthViewMode, monthlyData, monthlyWeeklyData]);

  // Transform data for line chart
  const transformDataForLineChart = useMemo(() => {
    if (selectedTab === "Day") return [];

    const data =
      selectedTab === "Week"
        ? peakData
        : selectedTab === "Month"
          ? monthChartData
          : selectedTab === "Quarter"
            ? quarterlyData
            : customRangeData;

    // Only include data points that have actual values
    const validData = data.filter((d) => d.hasData);

    // Create line data for each selected metric (exclude training load as it's shown as bars, exclude workloadFactor as it has its own y-axis)
    const lineData = Array.from(selectedMetrics)
      .filter(
        (metricKey) =>
          metricKey !== "trainingLoad" &&
          metricKey !== "workloadFactor" &&
          metricKey !== "hlr",
      ) // Exclude training load, load balance, and HLR from line charts (they have their own y-axes)
      .map((metricKey) => {
        const metric = availableMetrics.find((m) => m.key === metricKey);
        if (!metric) return null;

        let dataPoints;
        if (metricKey === "peakScore") {
          dataPoints = validData
            .filter((d) => d.score > 0)
            .map((d) => ({
              x: d.dateLabel,
              y: Math.round(d.score), // Round peak score to 0 decimal places
            }));
        } else {
          dataPoints = validData
            .filter((d) => d[metricKey] !== undefined && d[metricKey] > 0)
            .map((d) => ({
              x: d.dateLabel,
              y: Math.round(
                metricKey === "workloadFactor"
                  ? 100 - (d[metricKey] || 0) * 100 // Invert: lower wf (load spike) plots higher
                  : metricKey === "hlr" || metricKey === "sickFactor"
                    ? (d[metricKey] || 0) * 100 // Convert to percentage for display
                    : d[metricKey] || 0,
              ),
            }));
        }

        return {
          id: metric.label,
          data: dataPoints,
          color: metric.color,
        };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          data: { x: string; y: number }[];
          color: string;
        } => item !== null,
      );

    return lineData;
  }, [
    selectedTab,
    peakData,
    monthChartData,
    quarterlyData,
    customRangeData,
    selectedMetrics,
    availableMetrics,
  ]);

  // Handle back navigation - return to dashboard with the same date
  const handleBack = () => {
    const dateID = generateDateID(targetDate);
    // navigate(`/dashboard?date=${dateID}`);
    navigate(-1);
  };

  return (
    <div className="w-full min-h-screen bg-base-200 pt-16">
      <Header />
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 p-4">
          <div className="relative">
            <button
              onClick={() => setShowPremiumModal(false)}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm"
            >
              ✕
            </button>

            <ComingSoon featureKey="advancedGraphs" />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleBack}
                  className="p-2 rounded-full hover:bg-base-200 transition-colors bg-base-300"
                >
                  <ChevronLeftIcon className="h-5 w-5 text-base-content/80" />
                </button>
                <h1 className="text-2xl font-grotesk font-bold text-base-content">
                  Peak Score
                </h1>
              </div>
              <div className="flex space-x-2">
                {/* Export button hidden - feature not available yet */}
                {/* <button
                  onClick={() =>
                    isPremium
                      ? setShowExportDialog(true)
                      : setShowPremiumModal(true)
                  }
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Export wellness data"
                >
                  <ArrowDownTrayIcon
                    className={`h-4 w-4 ${
                      isPremium ? "text-base-content/60" : "text-blue-500"
                    }`}
                  />
                </button> */}
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Learn more about Peak Score"
                >
                  ?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-0 mx-auto max-w-4xl">
        <div className="flex justify-around border-b border-base-300">
          {(["Day", "Week", "Month", "Quarter"] as const).map((tab) => {
            const isRestricted =
              (tab === "Month" && !hasMonthlyAccess) ||
              (tab === "Quarter" && !hasQuarterlyAccess);
            return (
              <button
                key={tab}
                onClick={() => {
                  if (isRestricted) {
                    setShowPremiumModal(true);
                    return;
                  }
                  setSelectedTab(tab);
                }}
                className={`relative py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 flex-1 text-center flex items-center justify-center gap-1 ${
                  selectedTab === tab
                    ? "text-base-content border-b-2 border-base-content bg-base-300"
                    : "text-base-content/60 hover:text-base-content/80"
                }`}
              >
                {tab}
                {isRestricted && (
                  <span className="text-blue-500">
                    <LockClosedIcon className="w-3 h-3" />
                  </span>
                )}
              </button>
            );
          })}
          {/* Custom Date Range Icon Button */}
          <button
            onClick={() => setShowCustomDateModal(true)}
            className="relative py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 text-center flex items-center justify-center text-base-content/60 hover:text-base-content/80"
            title="Custom Date Range"
          >
            <CalendarIcon className="h-5 w-5 text-base-content/60" />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="flex flex-col items-center gap-6">
              {/* Large animated chevron */}
              <h1 className="text-base-content text-[8rem] font-groteskLogo animate-pulse-custom relative z-10">
                ^
              </h1>

              {/* Loading text */}
              <div className="text-center -mt-16">
                <p className="text-lg font-medium text-base-content font-grotesk">
                  Loading Peak Score data...
                </p>
                <p className="text-sm text-base-content/70 font-grotesk mt-2">
                  Analyzing your performance metrics
                </p>
              </div>

              {/* Loading progress indicator */}
              <div className="flex items-center gap-2 mt-4">
                <div className="w-32 h-1 bg-base-300 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse transition-all duration-1000 w-3/4"></div>
                </div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Content */}
          <div className="mx-auto max-w-4xl">
            {error && (
              <div className="bg-red-500/10 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Chart */}
            <div className="p-8 pt-0 relative">
              {((!hasMonthlyAccess && selectedTab === "Month") ||
                (!hasQuarterlyAccess && selectedTab === "Quarter")) && (
                <div className="absolute inset-0 z-10 flex items-start justify-center pt-8 px-4">
                  <ComingSoon featureKey="advancedGraphs" />
                </div>
              )}
              <div
                className={
                  (!hasQuarterlyAccess && selectedTab === "Quarter") ||
                  (!hasMonthlyAccess && selectedTab === "Month")
                    ? "blur-md pointer-events-none"
                    : ""
                }
              >
                <h2 className="text-xl font-heading font-semibold text-base-content mb-4">
                  {selectedTab === "Day" && ""}
                  {selectedTab === "Week" && ""}
                  {selectedTab === "Month" && ""}
                  {selectedTab === "Quarter" && ""}
                </h2>

                {selectedTab === "Day" ? (
                  // Day view - detailed peak breakdown
                  currentDayPeak ? (
                    <div className="space-y-4">
                      <div>
                        <h2 className="text-xl">
                          Peak Score{" "}
                          <span className="font-light">Breakdown</span>
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex justify-between bg-base-100 rounded-lg p-4 items-center">
                          <div className="text-sm text-base-content/70 font-grotesk">
                            Peak Score
                          </div>
                          <div className="text-2xl font-bold text-base-content font-heading">
                            {Math.round(currentDayPeak.peakScore)}
                          </div>
                        </div>
                        <div className="flex justify-between bg-base-100 rounded-lg p-4 items-center">
                          <div className="text-sm text-base-content/70 font-grotesk">
                            Wellness
                          </div>
                          <div className="text-2xl font-bold text-base-content font-heading">
                            {Math.round(currentDayPeak.wellnessFinal)}
                          </div>
                        </div>
                        <div className="flex justify-between bg-base-100 rounded-lg p-4 items-center">
                          <div className="text-sm text-base-content/70 font-grotesk">
                            Heart Lung Recovery Index (HLR)
                          </div>
                          {(() => {
                            const hlr = currentDayPeak.hlr;
                            const diff = Math.round(hlr * 100 - 100);
                            const title =
                              hlr > 1.07
                                ? "Well Above Normal"
                                : hlr >= 1.03
                                  ? "Above Normal"
                                  : hlr >= 1.0
                                    ? "Normal Optimal"
                                    : hlr >= 0.97
                                      ? "Normal Stable"
                                      : hlr >= 0.93
                                        ? "Below Normal Caution"
                                        : "Suppressed Recovery";
                            return (
                              <div className="flex flex-col items-end">
                                <span className="text-2xl font-bold text-base-content font-heading">{`${diff > 0 ? "+" : ""}${diff}%`}</span>
                                <span className="text-xs text-base-content/60">
                                  {title}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex justify-between bg-base-100 rounded-lg p-4 items-center">
                          <div className="text-sm text-base-content/70 font-grotesk">
                            Load Balance
                          </div>
                          {currentDayPeak.loadBalanceProvisional ? (
                            <span className="text-sm text-base-content/60">
                              Not enough data
                            </span>
                          ) : (
                            (() => {
                            const wf = currentDayPeak.workloadFactor;
                            const diff = Math.round(100 - wf * 100);
                            const title =
                              wf < 0.75
                                ? "Sharp Load Spike / High Stress"
                                : wf < 0.9
                                  ? "Elevated Load / High Challenge"
                                  : wf < 1.0
                                    ? "Optimal Load / Building Phase"
                                    : wf < 1.1
                                      ? "Optimal Load / Maintenance or Sharpening"
                                      : wf < 1.25
                                        ? "Reduced Load / Recovery or Taper"
                                        : "Underloading / Deconditioning Risk";
                            return (
                              <div className="flex flex-col items-end">
                                <span className="text-2xl font-bold text-base-content font-heading">{`${diff > 0 ? "+" : ""}${diff}%`}</span>
                                <span className="text-xs text-base-content/60">
                                  {title}
                                </span>
                              </div>
                            );
                          })())}
                        </div>
                        {/* <div className="flex justify-between bg-base-300 rounded-lg p-4 items-center">
                    <div className="text-sm text-base-content/70 font-grotesk">
                      Sick Factor
                    </div>
                    <div className="text-2xl font-bold text-base-content font-heading">
                      {Math.round(currentDayPeak.sickFactor * 100)}%
                    </div>
                  </div> */}
                        {/* <div className="flex justify-between bg-base-300 rounded-lg p-4 items-center">
                    <div className="text-sm text-base-content/70 font-grotesk">
                      EWMA Work A
                    </div>
                    <div className="text-2xl font-bold text-base-content font-heading">
                      {Math.round(currentDayPeak.EWMA_WorkA)}
                    </div>
                  </div>
                  <div className="flex justify-between bg-base-300 rounded-lg p-4 items-center">
                    <div className="text-sm text-base-content/70 font-grotesk">
                      EWMA Work C
                    </div>
                    <div className="text-2xl font-bold text-base-content font-heading">
                      {Math.round(currentDayPeak.EWMA_WorkC)}
                    </div>
                  </div> */}
                        {/* <div className="flex justify-between bg-base-300 rounded-lg p-4 items-center">
                    <div className="text-sm text-base-content/70 font-grotesk">
                      Wellness A
                    </div>
                    <div className="text-2xl font-bold text-base-content font-heading">
                      {Math.round(currentDayPeak.wellnessA)}
                    </div>
                  </div> */}
                      </div>
                      {currentDayPeak.notes && (
                        <div className="mt-4 p-4 bg-base-200 rounded-lg text-xs pb-0">
                          {/* <p className=" text-base-content/50 text-xs">
                            {new Date(currentDayPeak.notes!).toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              },
                            )}
                          </p> */}

                          <p className="font-grotesk mt-2">
                            If you have added more historical data after today's
                            score calculation, recalculate score.
                          </p>
                        </div>
                      )}

                      {/* Recalculate Button - Daily Section */}
                      <div className="">
                        {/* Error Messages */}
                        {recalcError && (
                          <div className="mb-3 bg-red-500/10 border border-red-200 rounded-md px-3 py-2">
                            <span className="text-sm text-red-600">
                              {recalcError}
                            </span>
                          </div>
                        )}
                        {bulkRecalcError && (
                          <div className="mb-3 bg-red-500/10 border border-red-200 rounded-md px-3 py-2">
                            <span className="text-sm text-red-600">
                              {bulkRecalcError}
                            </span>
                          </div>
                        )}

                        <div className="relative recalculate-dropdown">
                          <button
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            disabled={recalculating || bulkRecalculating}
                            className={`w-full px-4 py-3 rounded-lg text-sm font-medium border transition-colors flex items-center  gap-2 ${
                              recalculating || bulkRecalculating
                                ? "bg-base-200 text-base-content/60 border-base-300 cursor-not-allowed"
                                : "text-base-content border-none"
                            }`}
                          >
                            {recalculating || bulkRecalculating ? (
                              <span className="flex items-center gap-2">
                                <span className="loading loading-spinner loading-xs"></span>
                                {recalculating
                                  ? "Recalculating..."
                                  : `Recalculating (${Math.round(
                                      bulkProgress,
                                    )}%)`}
                              </span>
                            ) : (
                              <>
                                Recalculate
                                <ChevronDownIcon className="h-4 w-4" />
                              </>
                            )}
                          </button>

                          {dropdownOpen &&
                            !recalculating &&
                            !bulkRecalculating && (
                              <div className="absolute top-full left-0 mt-1 w-full bg-base-100 rounded-md shadow-lg border border-base-300 z-50">
                                <div className="py-1">
                                  <button
                                    onClick={() => {
                                      handleRecalculate();
                                      setDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 transition-colors"
                                  >
                                    <div className="font-medium">
                                      Recalculate Today
                                    </div>
                                    <div className="text-xs text-base-content/60">
                                      Recalculate peak score for{" "}
                                      <strong>today</strong>
                                    </div>
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleBulkRecalculate();
                                      setDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 transition-colors"
                                  >
                                    <div className="font-medium">
                                      Recalculate Last 90 Days
                                    </div>
                                    <div className="text-xs text-base-content/60">
                                      Bulk recalculate for historical data
                                    </div>
                                  </button>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-base-content/70">
                        No peak score data available for this day
                      </p>
                    </div>
                  )
                ) : // Week, Month, Quarter, and Custom view - line chart
                (selectedTab === "Week" && peakData.length > 0) ||
                  (selectedTab === "Month" && monthChartData.length > 0) ||
                  (selectedTab === "Quarter" && quarterlyData.length > 0) ||
                  (selectedTab === "Custom" && customRangeData.length > 0) ? (
                  <div className="px-1 md:px-4 pb-4">
                    {/* Metric Selector */}
                    <div
                      className="mb-3 mt-3 relative flex items-center justify-between"
                      ref={metricDropdownRef}
                    >
                      <button
                        onClick={() =>
                          setShowMetricDropdown(!showMetricDropdown)
                        }
                        className="flex items-center gap-2 px-3 py-2 bg-base-200 rounded-lg text-sm font-geist text-base-content/80 hover:bg-base-300 transition-colors"
                      >
                        <span>Metrics ({selectedMetrics.size})</span>
                        <ChevronDownIcon
                          className={`h-4 w-4 transition-transform ${showMetricDropdown ? "rotate-180" : ""}`}
                        />
                      </button>
                      {(selectedMetrics.size > 1 ||
                        !selectedMetrics.has("peakScore") ||
                        pinnedSlice) && (
                        <button
                          onClick={() => {
                            setSelectedMetrics(new Set(["peakScore"]));
                            setPinnedSlice(null);
                            setHoveredSlice(null);
                          }}
                          className="px-3 py-2 bg-base-200 rounded-lg text-sm font-geist text-base-content/60 hover:bg-base-300 hover:text-base-content/80 transition-colors"
                        >
                          Reset
                        </button>
                      )}
                      {showMetricDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg z-20 w-64 max-h-80 overflow-y-auto">
                          {availableMetrics.map((metric) => (
                            <label
                              key={metric.key}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-base-200 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="checkbox checkbox-xs"
                                checked={selectedMetrics.has(metric.key)}
                                onChange={() => {
                                  const newMetrics = new Set(selectedMetrics);
                                  if (selectedMetrics.has(metric.key)) {
                                    newMetrics.delete(metric.key);
                                  } else {
                                    newMetrics.add(metric.key);
                                  }
                                  setSelectedMetrics(newMetrics);
                                }}
                              />
                              <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: metric.color }}
                              />
                              <span className="text-sm font-geist text-base-content">
                                {metric.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Month view mode toggle */}
                    {selectedTab === "Month" && (
                      <div className="flex items-center gap-3 bg-base-200 rounded-lg p-1 mb-3 w-fit">
                        <button
                          onClick={() => setMonthViewMode("daily")}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                            monthViewMode === "daily"
                              ? "bg-base-100 text-base-content shadow-sm"
                              : "text-base-content/70 hover:text-base-content"
                          }`}
                        >
                          Daily
                        </button>
                        <button
                          onClick={() => setMonthViewMode("weekly")}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                            monthViewMode === "weekly"
                              ? "bg-base-100 text-base-content shadow-sm"
                              : "text-base-content/70 hover:text-base-content"
                          }`}
                        >
                          Weekly
                        </button>
                      </div>
                    )}

                    {/* Chart */}
                    {transformDataForLineChart.length > 0 &&
                    selectedMetrics.size > 0 ? (
                      <div className=" rounded-lg shadow-sm p-2">
                        {/* Selected metric legend */}
                        <div className="flex flex-wrap gap-2 px-4 pt-3 pb-2">
                          {Array.from(selectedMetrics).map((metricKey) => {
                            const metric = availableMetrics.find(
                              (m) => m.key === metricKey,
                            );
                            if (!metric) return null;
                            return (
                              <span
                                key={metricKey}
                                className="flex items-center gap-1.5 text-xs font-geist text-base-content/70"
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ backgroundColor: metric.color }}
                                />
                                {metric.label}
                              </span>
                            );
                          })}
                        </div>
                        <div className="overflow-x-auto -mx-2 px-2">
                          <div
                            className="h-80"
                            style={{
                              minWidth:
                                isMobile &&
                                ((selectedTab === "Month" &&
                                  monthViewMode === "daily") ||
                                  selectedTab === "Quarter")
                                  ? "700px"
                                  : "100%",
                            }}
                          >
                            <ResponsiveLine
                              theme={nivoTheme}
                              data={transformDataForLineChart}
                              margin={{
                                top: 40,
                                right:
                                  selectedMetrics.has("workloadFactor") ||
                                  selectedMetrics.has("hlr")
                                    ? 45
                                    : 25,
                                bottom: 60,
                                left: 25,
                              }}
                              xScale={{ type: "point" }}
                              yScale={{
                                type: "linear",
                                min: 0,
                                max: "auto",
                                stacked: false,
                              }}
                              curve="cardinal"
                              enablePoints={true}
                              axisTop={null}
                              axisRight={
                                selectedMetrics.has("workloadFactor")
                                  ? {
                                      tickSize: 0,
                                      tickPadding: 10,
                                      tickRotation: 0,
                                      legend: "",
                                      legendPosition: "middle" as const,
                                      legendOffset: 45,
                                      tickValues: [],
                                      format: () => "",
                                    }
                                  : null
                              }
                              axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: -45,
                                legend: "",
                                legendPosition: "middle",
                                legendOffset: 50,
                                // Hide tick labels on mobile for Month daily view (28 days)
                                format: (value) => {
                                  if (
                                    selectedTab === "Month" &&
                                    isMobile &&
                                    monthViewMode === "daily"
                                  ) {
                                    return ""; // Hide all labels on mobile for Month daily view
                                  }
                                  return value;
                                },
                              }}
                              axisLeft={null}
                              enableGridX={false}
                              enableGridY={true}
                              colors={(d: any) =>
                                d.color || (isDark ? "#ffffff" : "#374151")
                              }
                              lineWidth={3}
                              pointSize={3}
                              pointBorderWidth={0.5}
                              pointBorderColor="#ffffff"
                              enablePointLabel={false}
                              enableArea={selectedMetrics.size === 1}
                              areaOpacity={1}
                              defs={[
                                {
                                  id: "gradientArea",
                                  type: "linearGradient",
                                  colors: [
                                    {
                                      offset: 0,
                                      color: "#3b82f6",
                                      opacity: 0.4,
                                    },
                                    {
                                      offset: 100,
                                      color: "#3b82f6",
                                      opacity: 0.1,
                                    },
                                  ],
                                },
                              ]}
                              fill={[{ match: "*", id: "gradientArea" }]}
                              useMesh={true}
                              enableSlices="x"
                              onClick={(point: any) => {
                                // When clicking on the chart, pin the current slice
                                if (hoveredSlice) {
                                  setPinnedSlice(hoveredSlice);
                                }
                              }}
                              onMouseLeave={() => {
                                setHoveredSlice(null);
                              }}
                              sliceTooltip={({ slice }: any) => {
                                // Use setTimeout to update state after render
                                setTimeout(() => setHoveredSlice(slice), 0);
                                return null;
                              }}
                              layers={[
                                "markers",
                                "axes",
                                "areas",
                                "crosshair",
                                // Custom layer for sickness/injury icons at the top of the chart
                                ({ xScale, innerHeight }: any) => {
                                  const chartData =
                                    selectedTab === "Quarter"
                                      ? quarterlyData
                                      : selectedTab === "Month"
                                        ? monthChartData
                                        : selectedTab === "Custom"
                                          ? customRangeData
                                          : peakData;

                                  const flaggedDays = chartData.filter(
                                    (d) =>
                                      d.hasData &&
                                      (d.sick ||
                                        (d.injuryPenalty &&
                                          d.injuryPenalty > 0) ||
                                        d.niggle),
                                  );

                                  if (flaggedDays.length === 0) return null;

                                  return (
                                    <g key="sick-injury-icons">
                                      {flaggedDays.map((d, i) => {
                                        const x = xScale(d.dateLabel);
                                        if (x === undefined) return null;
                                        const isSick = d.sick;
                                        const isInjured =
                                          d.injuryPenalty &&
                                          d.injuryPenalty > 0;
                                        const isNiggle = d.niggle;
                                        // Stack dots vertically
                                        const spacing = 12;
                                        let yIdx = 0;
                                        return (
                                          <g
                                            key={`flag-${i}`}
                                            transform={`translate(${x}, -2)`}
                                          >
                                            {isInjured && (
                                              <circle
                                                cx={0}
                                                cy={-5 - yIdx++ * spacing}
                                                r={5}
                                                fill="#dc2626"
                                                stroke="white"
                                                strokeWidth={1.5}
                                                style={{ cursor: "default" }}
                                              />
                                            )}
                                            {isNiggle && (
                                              <circle
                                                cx={0}
                                                cy={-5 - yIdx++ * spacing}
                                                r={5}
                                                fill="#f97316"
                                                stroke="white"
                                                strokeWidth={1.5}
                                                style={{ cursor: "default" }}
                                              />
                                            )}
                                            {isSick && (
                                              <circle
                                                cx={0}
                                                cy={-5 - yIdx++ * spacing}
                                                r={5}
                                                fill="#f59e0b"
                                                stroke="white"
                                                strokeWidth={1.5}
                                                style={{ cursor: "default" }}
                                              />
                                            )}
                                          </g>
                                        );
                                      })}
                                    </g>
                                  );
                                },
                                // Custom layer for training load bars (all tabs when training load is selected) - render before lines so they appear behind
                                ...(selectedMetrics.has("trainingLoad")
                                  ? [
                                      ({
                                        xScale,
                                        yScale,
                                        width,
                                        height,
                                      }: any) => {
                                        // Use appropriate data source based on selected tab
                                        const trainingLoadData =
                                          selectedTab === "Quarter"
                                            ? quarterlyData
                                            : selectedTab === "Month"
                                              ? monthChartData
                                              : selectedTab === "Custom"
                                                ? customRangeData
                                                : peakData;

                                        const maxTrainingLoad = Math.max(
                                          ...trainingLoadData
                                            .filter((d) => d.hasData)
                                            .map((d) => d.trainingLoad || 0),
                                        );

                                        if (maxTrainingLoad === 0) return null;

                                        return (
                                          <g key="training-load-bars">
                                            {trainingLoadData.map(
                                              (dataPoint, index) => {
                                                if (!dataPoint.hasData)
                                                  return null;

                                                const xPos = xScale(
                                                  dataPoint.dateLabel,
                                                );
                                                // Responsive bar width based on selected tab and screen size
                                                const getResponsiveBarWidth =
                                                  () => {
                                                    if (
                                                      selectedTab === "Month"
                                                    ) {
                                                      if (
                                                        monthViewMode ===
                                                        "weekly"
                                                      ) {
                                                        // Weekly mode - 4 bars, use wider widths
                                                        if (isMobile) return 35;
                                                        if (isTablet) return 45;
                                                        return 50;
                                                      }
                                                      // Daily mode - 28 days, make bars narrower on mobile
                                                      if (isMobile)
                                                        return Math.max(
                                                          8,
                                                          screenWidth / 40,
                                                        ); // Minimum 8px, scale with screen
                                                      if (isTablet)
                                                        return Math.max(
                                                          12,
                                                          screenWidth / 60,
                                                        ); // Minimum 12px
                                                      return 20; // Desktop: reasonable width for 28 bars
                                                    } else if (
                                                      selectedTab === "Week"
                                                    ) {
                                                      // Week view - wider bars for better visibility
                                                      if (isMobile) return 35;
                                                      if (isTablet) return 45;
                                                      return 50; // Desktop
                                                    } else {
                                                      // Quarter view - match Training Load chart width
                                                      return 25; // Same as Training Insights Quarter view
                                                    }
                                                  };
                                                const barWidth =
                                                  getResponsiveBarWidth();
                                                const barX =
                                                  xPos - barWidth / 2;

                                                // Scale bar height relative to max training load (scale to 100 on y-axis)
                                                const trainingLoadValue =
                                                  dataPoint.trainingLoad || 0;
                                                const barValue =
                                                  maxTrainingLoad > 0
                                                    ? (trainingLoadValue /
                                                        maxTrainingLoad) *
                                                      100
                                                    : 0; // Max bar represents 100 on y-scale, others scale proportionally
                                                const barHeight =
                                                  yScale(0) - yScale(barValue); // Height in chart coordinates

                                                return (
                                                  <g key={`bar-group-${index}`}>
                                                    {/* Bar */}
                                                    <rect
                                                      key={`bar-${index}`}
                                                      x={barX}
                                                      y={yScale(barValue)}
                                                      width={barWidth}
                                                      height={barHeight}
                                                      fill="#9ca3af"
                                                      opacity={0.6}
                                                    />
                                                  </g>
                                                );
                                              },
                                            )}
                                          </g>
                                        );
                                      },
                                    ]
                                  : []),
                                // Custom layer for Load Balance line (secondary y-axis on right)
                                ...(selectedMetrics.has("workloadFactor")
                                  ? [
                                      ({
                                        xScale,
                                        innerHeight,
                                        innerWidth,
                                      }: any) => {
                                        const chartData =
                                          selectedTab === "Quarter"
                                            ? quarterlyData
                                            : selectedTab === "Month"
                                              ? monthChartData
                                              : selectedTab === "Custom"
                                                ? customRangeData
                                                : peakData;

                                        // Create a custom y-scale for workload factor (70% to 130%) - inverted so lower wf (overloading) plots higher
                                        const wfYScale = (value: number) => {
                                          const min = 0.7;
                                          const max = 1.3;
                                          const normalized =
                                            (value - min) / (max - min);
                                          return normalized * innerHeight;
                                        };

                                        // Filter data points that have workload factor
                                        const wfDataPoints = chartData
                                          .filter(
                                            (d) =>
                                              d.hasData &&
                                              d.workloadFactor &&
                                              d.workloadFactor > 0,
                                          )
                                          .map((d) => ({
                                            x: xScale(d.dateLabel),
                                            y: wfYScale(d.workloadFactor!),
                                            value: d.workloadFactor!,
                                          }))
                                          .filter(
                                            (
                                              d,
                                            ): d is {
                                              x: number;
                                              y: number;
                                              value: number;
                                            } => d.y !== null,
                                          );

                                        if (wfDataPoints.length === 0)
                                          return null;

                                        // Create smooth cardinal spline path (matching Nivo's curve="cardinal")
                                        const cardinalSpline = (
                                          points: { x: number; y: number }[],
                                          tension = 0.5,
                                        ) => {
                                          if (points.length < 2) return "";
                                          if (points.length === 2)
                                            return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
                                          let path = `M ${points[0].x},${points[0].y}`;
                                          for (
                                            let i = 0;
                                            i < points.length - 1;
                                            i++
                                          ) {
                                            const p0 =
                                              points[Math.max(0, i - 1)];
                                            const p1 = points[i];
                                            const p2 = points[i + 1];
                                            const p3 =
                                              points[
                                                Math.min(
                                                  points.length - 1,
                                                  i + 2,
                                                )
                                              ];
                                            const cp1x =
                                              p1.x +
                                              (p2.x - p0.x) / (6 / tension);
                                            const cp1y =
                                              p1.y +
                                              (p2.y - p0.y) / (6 / tension);
                                            const cp2x =
                                              p2.x -
                                              (p3.x - p1.x) / (6 / tension);
                                            const cp2y =
                                              p2.y -
                                              (p3.y - p1.y) / (6 / tension);
                                            path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
                                          }
                                          return path;
                                        };
                                        const pathData =
                                          cardinalSpline(wfDataPoints);

                                        // Right-side y-axis labels
                                        const axisLabels = [
                                          { value: 0.7, label: "+30%" },
                                          { value: 0.85, label: "+15%" },
                                          { value: 1.0, label: "0%" },
                                          { value: 1.15, label: "-15%" },
                                          { value: 1.3, label: "-30%" },
                                        ];

                                        return (
                                          <g key="workload-factor-line">
                                            {/* Right axis labels */}
                                            {axisLabels.map((a) => (
                                              <text
                                                key={`wf-axis-${a.value}`}
                                                x={innerWidth + 8}
                                                y={wfYScale(a.value)}
                                                textAnchor="start"
                                                dominantBaseline="middle"
                                                fontSize={10}
                                                fill="oklch(var(--bc) / 0.5)"
                                              >
                                                {a.label}
                                              </text>
                                            ))}
                                            {/* Load Balance Line */}
                                            <path
                                              d={pathData}
                                              fill="none"
                                              stroke={
                                                isDark ? "#ffffff" : "#374151"
                                              }
                                              strokeWidth={3}
                                            />
                                            {/* Points on the line */}
                                            {wfDataPoints.map((d, i) => (
                                              <circle
                                                key={`wf-point-${i}`}
                                                cx={d.x}
                                                cy={d.y}
                                                r={4}
                                                fill={
                                                  isDark ? "#ffffff" : "#374151"
                                                }
                                                stroke="white"
                                                strokeWidth={1}
                                              />
                                            ))}
                                          </g>
                                        );
                                      },
                                    ]
                                  : []),
                                // Custom layer for HLR line (secondary y-axis on right)
                                ...(selectedMetrics.has("hlr")
                                  ? [
                                      ({
                                        xScale,
                                        innerHeight,
                                        innerWidth,
                                      }: any) => {
                                        const chartData =
                                          selectedTab === "Quarter"
                                            ? quarterlyData
                                            : selectedTab === "Month"
                                              ? monthChartData
                                              : selectedTab === "Custom"
                                                ? customRangeData
                                                : peakData;

                                        // Create a custom y-scale for HLR (0.8 to 1.2 range) - matching SleepInsights
                                        const hlrYScale = (value: number) => {
                                          const min = 0.8;
                                          const max = 1.2;
                                          const normalized =
                                            (value - min) / (max - min);
                                          return (
                                            innerHeight -
                                            normalized * innerHeight
                                          );
                                        };

                                        // Filter data points that have HLR
                                        const hlrDataPoints = chartData
                                          .filter(
                                            (d) =>
                                              d.hasData && d.hlr && d.hlr > 0,
                                          )
                                          .map((d) => ({
                                            x: xScale(d.dateLabel),
                                            y: hlrYScale(d.hlr!),
                                            value: d.hlr!,
                                          }))
                                          .filter(
                                            (
                                              d,
                                            ): d is {
                                              x: number;
                                              y: number;
                                              value: number;
                                            } => d.y !== null,
                                          );

                                        if (hlrDataPoints.length === 0)
                                          return null;

                                        // Create smooth cardinal spline path (matching Nivo's curve="cardinal")
                                        const cardinalSpline = (
                                          points: { x: number; y: number }[],
                                          tension = 0.5,
                                        ) => {
                                          if (points.length < 2) return "";
                                          if (points.length === 2)
                                            return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
                                          let path = `M ${points[0].x},${points[0].y}`;
                                          for (
                                            let i = 0;
                                            i < points.length - 1;
                                            i++
                                          ) {
                                            const p0 =
                                              points[Math.max(0, i - 1)];
                                            const p1 = points[i];
                                            const p2 = points[i + 1];
                                            const p3 =
                                              points[
                                                Math.min(
                                                  points.length - 1,
                                                  i + 2,
                                                )
                                              ];
                                            const cp1x =
                                              p1.x +
                                              (p2.x - p0.x) / (6 / tension);
                                            const cp1y =
                                              p1.y +
                                              (p2.y - p0.y) / (6 / tension);
                                            const cp2x =
                                              p2.x -
                                              (p3.x - p1.x) / (6 / tension);
                                            const cp2y =
                                              p2.y -
                                              (p3.y - p1.y) / (6 / tension);
                                            path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
                                          }
                                          return path;
                                        };
                                        const pathData =
                                          cardinalSpline(hlrDataPoints);

                                        // Right-side y-axis labels (only show if Load Balance isn't also selected, to avoid overlap)
                                        const showAxisLabels =
                                          !selectedMetrics.has(
                                            "workloadFactor",
                                          );
                                        const axisLabels = [
                                          { value: 0.8, label: "-20%" },
                                          { value: 0.9, label: "-10%" },
                                          { value: 1.0, label: "0%" },
                                          { value: 1.1, label: "+10%" },
                                          { value: 1.2, label: "+20%" },
                                        ];

                                        return (
                                          <g key="hlr-line">
                                            {/* Right axis labels */}
                                            {showAxisLabels &&
                                              axisLabels.map((a) => (
                                                <text
                                                  key={`hlr-axis-${a.value}`}
                                                  x={innerWidth + 8}
                                                  y={hlrYScale(a.value)}
                                                  textAnchor="start"
                                                  dominantBaseline="middle"
                                                  fontSize={10}
                                                  fill="oklch(var(--bc) / 0.5)"
                                                >
                                                  {a.label}
                                                </text>
                                              ))}
                                            {/* HLR Line */}
                                            <path
                                              d={pathData}
                                              fill="none"
                                              stroke="#8b5cf6"
                                              strokeWidth={3}
                                            />
                                            {/* Points on the line */}
                                            {hlrDataPoints.map((d, i) => (
                                              <circle
                                                key={`hlr-point-${i}`}
                                                cx={d.x}
                                                cy={d.y}
                                                r={4}
                                                fill="#8b5cf6"
                                                stroke="white"
                                                strokeWidth={1}
                                              />
                                            ))}
                                          </g>
                                        );
                                      },
                                    ]
                                  : []),
                                "lines",
                                "points",
                                "slices",
                                "mesh",
                                // Custom layer for average lines - only show when Peak Score is the only selected metric
                                ({
                                  xScale,
                                  yScale,
                                  margin,
                                  width,
                                  height,
                                }: any) => (
                                  <g key="average-lines">
                                    {selectedMetrics.size === 1 &&
                                      selectedMetrics.has("peakScore") &&
                                      selectedTab !== "Quarter" && (
                                        <>
                                          {/* 7-Day Average Line (Purple) */}
                                          {averageLines.sevenDay > 0 && (
                                            <g>
                                              <line
                                                x1={0}
                                                y1={yScale(
                                                  averageLines.sevenDay,
                                                )}
                                                x2={width}
                                                y2={yScale(
                                                  averageLines.sevenDay,
                                                )}
                                                stroke="#7c3aed"
                                                strokeWidth={2}
                                                strokeDasharray="8,4"
                                              />
                                            </g>
                                          )}
                                          {/* 28-Day Average Line (Orange) */}
                                          {averageLines.fourWeek > 0 && (
                                            <g>
                                              <line
                                                x1={0}
                                                y1={yScale(
                                                  averageLines.fourWeek,
                                                )}
                                                x2={width}
                                                y2={yScale(
                                                  averageLines.fourWeek,
                                                )}
                                                stroke="#f97316"
                                                strokeWidth={2}
                                                strokeDasharray="8,4"
                                              />
                                            </g>
                                          )}
                                        </>
                                      )}
                                  </g>
                                ),
                              ]}
                              animate={true}
                            />
                          </div>
                        </div>

                        {/* Average indicators below chart */}
                        {(averageLines.sevenDay > 0 ||
                          averageLines.fourWeek > 0) &&
                          selectedMetrics.size === 1 &&
                          selectedMetrics.has("peakScore") && (
                            <div className="mt-4 text-sm bg-base-200 p-2 px-4 rounded-md">
                              {averageLines.sevenDay > 0 && (
                                <div className="flex items-center gap-2">
                                  <span className="font-heading text-purple-600">
                                    {Math.round(averageLines.sevenDay)}
                                  </span>{" "}
                                  <div
                                    className="w-8 h-0.5"
                                    style={{
                                      background:
                                        "repeating-linear-gradient(to right, #7c3aed 0, #7c3aed 8px, transparent 8px, transparent 12px)",
                                    }}
                                  ></div>
                                  <span className="text-base-content/60 font-medium text-xs">
                                    7 Day Daily Average
                                  </span>
                                </div>
                              )}
                              {averageLines.fourWeek > 0 && (
                                <div className="flex items-center gap-2">
                                  <span className="font-heading text-orange-600">
                                    {Math.round(averageLines.fourWeek)}
                                  </span>{" "}
                                  <div
                                    className="w-8 h-0.5"
                                    style={{
                                      background:
                                        "repeating-linear-gradient(to right, #f97316 0, #f97316 8px, transparent 8px, transparent 12px)",
                                    }}
                                  ></div>
                                  <span className="text-base-content/60 font-medium text-xs">
                                    28 Day Average
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                        {/* Fixed metric values display */}
                        <div className="mt-4 bg-base-200/50 rounded-lg p-4">
                          {(() => {
                            // Use pinned slice if available, otherwise use hovered slice
                            const displaySlice = hoveredSlice || pinnedSlice;

                            if (!displaySlice || !displaySlice.points) {
                              return (
                                <div className="text-sm text-base-content/60 text-center">
                                  Hover over the graph to see values
                                </div>
                              );
                            }
                            return (
                              <>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="font-medium text-base-content">
                                    {displaySlice.points[0].data.x}
                                  </div>
                                  {pinnedSlice && !hoveredSlice && (
                                    <span className="text-xs text-base-content/60 italic">
                                      (pinned)
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  {displaySlice.points.map((point: any) => {
                                    const serieName = point.seriesId;
                                    const serieColor =
                                      point.color || point.serieColor;

                                    const displayValue = point.data.y;

                                    return (
                                      <div
                                        key={point.id}
                                        className="flex items-center justify-between"
                                      >
                                        <div className="flex items-center gap-2">
                                          <div
                                            className="w-4 h-4 rounded-full"
                                            style={{
                                              backgroundColor: serieColor,
                                            }}
                                          />
                                          <span className="text-sm font-medium text-base-content/80">
                                            {serieName}
                                          </span>
                                        </div>
                                        <span
                                          className="text-lg font-bold"
                                          style={{ color: serieColor }}
                                        >
                                          {Math.round(displayValue)}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  {/* Add Training Load if it's selected */}
                                  {selectedMetrics.has("trainingLoad") &&
                                    (() => {
                                      const data =
                                        selectedTab === "Week"
                                          ? peakData
                                          : selectedTab === "Month"
                                            ? monthChartData
                                            : selectedTab === "Quarter"
                                              ? quarterlyData
                                              : customRangeData;
                                      const dateLabel =
                                        displaySlice.points[0].data.x;
                                      const dataPoint = data.find(
                                        (d) => d.dateLabel === dateLabel,
                                      );
                                      const trainingLoadValue =
                                        dataPoint?.trainingLoad || 0;
                                      const trainingLoadMetric =
                                        availableMetrics.find(
                                          (m) => m.key === "trainingLoad",
                                        );

                                      if (
                                        trainingLoadValue > 0 &&
                                        trainingLoadMetric
                                      ) {
                                        return (
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-4 h-4 rounded-full"
                                                style={{
                                                  backgroundColor:
                                                    trainingLoadMetric.color,
                                                }}
                                              />
                                              <span className="text-sm font-medium text-base-content/80">
                                                {trainingLoadMetric.label}
                                              </span>
                                            </div>
                                            <span
                                              className="text-lg font-bold"
                                              style={{
                                                color: trainingLoadMetric.color,
                                              }}
                                            >
                                              {Math.round(trainingLoadValue)}
                                            </span>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                  {/* Add Load Balance if it's selected */}
                                  {selectedMetrics.has("workloadFactor") &&
                                    (() => {
                                      const data =
                                        selectedTab === "Week"
                                          ? peakData
                                          : selectedTab === "Month"
                                            ? monthChartData
                                            : selectedTab === "Quarter"
                                              ? quarterlyData
                                              : customRangeData;
                                      const dateLabel =
                                        displaySlice.points[0].data.x;
                                      const dataPoint = data.find(
                                        (d) => d.dateLabel === dateLabel,
                                      );
                                      const wf = dataPoint?.workloadFactor || 0;
                                      const loadBalanceMetric =
                                        availableMetrics.find(
                                          (m) => m.key === "workloadFactor",
                                        );

                                      if (wf > 0 && loadBalanceMetric) {
                                        const diff = Math.round(100 - wf * 100);
                                        const title =
                                          wf < 0.75
                                            ? "Sharp Load Spike / High Stress"
                                            : wf < 0.9
                                              ? "Elevated Load / High Challenge"
                                              : wf < 1.0
                                                ? "Optimal Load / Building Phase"
                                                : wf < 1.1
                                                  ? "Optimal Load / Maintenance or Sharpening"
                                                  : wf < 1.25
                                                    ? "Reduced Load / Recovery or Taper"
                                                    : "Underloading / Deconditioning Risk";
                                        return (
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-4 h-4 rounded-full"
                                                style={{
                                                  backgroundColor:
                                                    loadBalanceMetric.color,
                                                }}
                                              />
                                              <span className="text-sm font-medium text-base-content/80">
                                                {loadBalanceMetric.label}
                                              </span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                              <span
                                                className="text-lg font-bold"
                                                style={{
                                                  color:
                                                    loadBalanceMetric.color,
                                                }}
                                              >{`${diff > 0 ? "+" : ""}${diff}%`}</span>
                                              <span className="text-xs text-base-content/60">
                                                {title}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                  {/* Add HLR if it's selected */}
                                  {selectedMetrics.has("hlr") &&
                                    (() => {
                                      const data =
                                        selectedTab === "Week"
                                          ? peakData
                                          : selectedTab === "Month"
                                            ? monthChartData
                                            : selectedTab === "Quarter"
                                              ? quarterlyData
                                              : customRangeData;
                                      const dateLabel =
                                        displaySlice.points[0].data.x;
                                      const dataPoint = data.find(
                                        (d) => d.dateLabel === dateLabel,
                                      );
                                      const hlr = dataPoint?.hlr || 0;
                                      const hlrMetric = availableMetrics.find(
                                        (m) => m.key === "hlr",
                                      );

                                      if (hlr > 0 && hlrMetric) {
                                        const diff = Math.round(
                                          hlr * 100 - 100,
                                        );
                                        const title =
                                          hlr > 1.07
                                            ? "Well Above Normal"
                                            : hlr >= 1.03
                                              ? "Above Normal"
                                              : hlr >= 1.0
                                                ? "Normal Optimal"
                                                : hlr >= 0.97
                                                  ? "Normal Stable"
                                                  : hlr >= 0.93
                                                    ? "Below Normal Caution"
                                                    : "Suppressed Recovery";
                                        return (
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-4 h-4 rounded-full"
                                                style={{
                                                  backgroundColor:
                                                    hlrMetric.color,
                                                }}
                                              />
                                              <span className="text-sm font-medium text-base-content/80">
                                                {hlrMetric.label}
                                              </span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                              <span
                                                className="text-lg font-bold"
                                                style={{
                                                  color: hlrMetric.color,
                                                }}
                                              >{`${diff > 0 ? "+" : ""}${diff}%`}</span>
                                              <span className="text-xs text-base-content/60">
                                                {title}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                  {/* Sickness & Injury indicators */}
                                  {(() => {
                                    const data =
                                      selectedTab === "Week"
                                        ? peakData
                                        : selectedTab === "Month"
                                          ? monthChartData
                                          : selectedTab === "Quarter"
                                            ? quarterlyData
                                            : customRangeData;
                                    const dateLabel =
                                      displaySlice.points[0].data.x;
                                    const dataPoint = data.find(
                                      (d) => d.dateLabel === dateLabel,
                                    );
                                    const isSick = dataPoint?.sick === true;
                                    const isInjured =
                                      dataPoint?.injuryPenalty !== undefined &&
                                      dataPoint.injuryPenalty > 0;
                                    const isNiggle = dataPoint?.niggle === true;

                                    return (
                                      <>
                                        {isSick && (
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div className="w-4 h-4 rounded-full bg-amber-500" />
                                              <span className="text-sm font-medium text-base-content/80">
                                                Sickness
                                              </span>
                                            </div>
                                            <span className="text-lg font-bold text-amber-500">
                                              Flagged
                                            </span>
                                          </div>
                                        )}
                                        {isInjured && (
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-4 h-4 rounded-full"
                                                style={{
                                                  backgroundColor: "#dc2626",
                                                }}
                                              />
                                              <span className="text-sm font-medium text-base-content/80">
                                                Injury
                                              </span>
                                            </div>
                                            <span
                                              className="text-lg font-bold"
                                              style={{ color: "#dc2626" }}
                                            >
                                              Flagged
                                            </span>
                                          </div>
                                        )}
                                        {isNiggle && (
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-4 h-4 rounded-full"
                                                style={{
                                                  backgroundColor: "#f97316",
                                                }}
                                              />
                                              <span className="text-sm font-medium text-base-content/80">
                                                Niggle
                                              </span>
                                            </div>
                                            <span
                                              className="text-lg font-bold"
                                              style={{ color: "#f97316" }}
                                            >
                                              Flagged
                                            </span>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-base-content/70 text-sm">
                          {selectedMetrics.size === 0
                            ? "Select at least one metric to display"
                            : "No peak score data available for this period"}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-base-content/70">
                      No peak score data available for this period
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bulk Recalculation Progress Dialog */}
          {showProgressDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-base-100 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
                {/* Dialog Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-base-content">
                    Recalculating Peak Scores
                  </h3>
                  <button
                    onClick={() =>
                      !bulkRecalculating && setShowProgressDialog(false)
                    }
                    disabled={bulkRecalculating}
                    className={`p-2 rounded-full transition-colors ${
                      bulkRecalculating
                        ? "text-base-content/50 cursor-not-allowed"
                        : "text-base-content/60 hover:text-base-content/80 hover:bg-base-200"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-base-content/80">
                      Progress: {Math.round(bulkProgress)}%
                    </span>
                    <span className="text-sm text-base-content/60">
                      {dateResults.filter((d) => d.status === "success").length}{" "}
                      of {dateResults.length} completed
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${bulkProgress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Results List */}
                <div className="flex-1 overflow-y-auto border rounded-lg">
                  <div className="divide-y divide-base-300">
                    {dateResults.map((result, index) => (
                      <div
                        key={result.dateId}
                        className={`p-3 flex items-center justify-between ${
                          result.status === "processing" ? "bg-blue-500/10" : ""
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          {/* Status Icon */}
                          <div className="flex-shrink-0">
                            {result.status === "pending" && (
                              <div className="w-4 h-4 rounded-full border-2 border-base-300"></div>
                            )}
                            {result.status === "processing" && (
                              <div className="w-4 h-4">
                                <span className="loading loading-spinner loading-xs text-blue-600"></span>
                              </div>
                            )}
                            {result.status === "success" && (
                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                <svg
                                  className="w-2.5 h-2.5 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                            {result.status === "error" && (
                              <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                                <svg
                                  className="w-2.5 h-2.5 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Date and Status */}
                          <div>
                            <div className="text-sm font-medium text-base-content">
                              {result.dateLabel}
                            </div>
                            <div className="text-xs text-base-content/60">
                              {result.dateId}
                            </div>
                          </div>
                        </div>

                        {/* Result */}
                        <div className="text-right">
                          {result.status === "success" &&
                            result.peakScore !== undefined && (
                              <div className="text-sm font-medium text-green-600">
                                Peak Score: {Math.round(result.peakScore)}
                              </div>
                            )}
                          {result.status === "error" && (
                            <div
                              className="text-xs text-red-600 max-w-xs truncate"
                              title={result.error}
                            >
                              {result.error}
                            </div>
                          )}
                          {result.status === "processing" && (
                            <div className="text-xs text-blue-600">
                              Processing...
                            </div>
                          )}
                          {result.status === "pending" && (
                            <div className="text-xs text-base-content/50">
                              Waiting...
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dialog Footer */}
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-base-content/60">
                    {bulkRecalculating ? (
                      <span className="flex items-center gap-2">
                        <span className="loading loading-spinner loading-xs"></span>
                        Recalculating peak scores...
                      </span>
                    ) : (
                      "Recalculation complete"
                    )}
                  </div>
                  <button
                    onClick={() => setShowProgressDialog(false)}
                    disabled={bulkRecalculating}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      bulkRecalculating
                        ? "bg-base-200 text-base-content/60 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    {bulkRecalculating ? "Processing..." : "Close"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Info Dialog */}
      {showInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 sm:p-4 backdrop-blur-sm">
          <div className="bg-base-100 sm:rounded-lg w-full h-full sm:h-auto sm:max-w-lg sm:max-h-[80vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-grotesk  text-base-content">
                  Peak Score
                </h3>
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="p-1 rounded-full hover:bg-base-200 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-base-content/60" />
                </button>
              </div>

              <div className="space-y-4 text-sm text-base-content/80">
                <p className="font-grotesk text-lg leading-tight">
                  Peak Score is a readiness metric that comprehensively
                  evaluates your overall readiness and performance capacity
                  based on multiple factors.
                </p>

                <div>
                  <h4 className="font-bold text-lg font-grotesk text-base-content mb-2">
                    What factors are considered?
                  </h4>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>
                      Wellness metrics (mood, stress, health, recovery and
                      physical)
                    </li>
                    <li>
                      Heart Lung Recovery Index (HLR) - How well your
                      cardiorespiratory system is adapting
                    </li>
                    <li>
                      Load Balance (Recent v Long term training load
                      calculation)
                    </li>
                    <li>Sleep Quality and Duration</li>
                    <li>Training frequency</li>
                    <li>Soreness and illness factors</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-lg font-grotesk text-base-content mb-2">
                    Score interpretation & color guide:
                  </h4>
                  <div className="space-y-3">
                    <div className="p-4">
                      {/* Color gradient bar */}
                      <div
                        className="h-3 rounded-full w-full"
                        style={{
                          background:
                            "linear-gradient(to right, #ef4444 0%, #ef4444 27.27%, #f97316 27.27%, #f97316 54.55%, #22c55e 54.55%, #22c55e 72.73%, #0096c7 72.73%, #0096c7 90.91%, #DAA520 90.91%, #DAA520 100%)",
                        }}
                      />
                      {/* Score markers */}
                      <div className="relative text-xs text-base-content/60 font-medium mt-2">
                        <span className="absolute" style={{ left: "0%" }}>
                          0
                        </span>
                        <span className="absolute" style={{ left: "27.27%" }}>
                          30
                        </span>
                        <span className="absolute" style={{ left: "54.55%" }}>
                          60
                        </span>
                        <span className="absolute" style={{ left: "72.73%" }}>
                          80
                        </span>
                        <span className="absolute" style={{ left: "90.91%" }}>
                          100
                        </span>
                        <span
                          className="absolute"
                          style={{
                            left: "100%",
                            transform: "translateX(-100%)",
                          }}
                        >
                          110
                        </span>
                      </div>
                    </div>
                    <br></br>
                    {/* Combined color descriptions with interpretations */}
                    <div className="grid grid-cols-1 gap-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#DAA520] flex-shrink-0"></div>
                        <span>
                          <strong>Golden Zone (&gt;100):</strong> Ideal
                          conditions for possible personal best today.
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#0096c7] flex-shrink-0"></div>
                        <span>
                          <strong>Excellent Readiness (80-100):</strong>
                          Optimal for high-intensity training
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#22c55e] flex-shrink-0"></div>
                        <span>
                          <strong>Good Readiness for building (60-79):</strong>
                          Suitable for all training but monitor warm up.
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#f97316] flex-shrink-0"></div>
                        <span>
                          <strong>Average Readiness (30-59):</strong>
                          Room for improvement in wellness & training load
                          balance. If training, monitor warm up and possibly
                          reduce training load and intensity today.
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#ef4444] flex-shrink-0"></div>
                        <span>
                          <strong>Strong Caution (0-29):</strong> Low readiness,
                          prioritise recovery and rest today
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="px-4 py-2 bg-neutral text-neutral-content rounded-md hover:bg-neutral/90 transition-colors font-medium font-grotesk"
                >
                  Okay, got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg max-w-md w-full">
            <div className="p-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-grotesk font-semibold text-base-content">
                  Custom Date Range
                </h3>
                <button
                  onClick={() => {
                    setShowCustomDateModal(false);
                    if (customRangeData.length === 0) {
                      setSelectedTab("Day");
                    }
                  }}
                  className="p-1 rounded-full hover:bg-base-200 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-base-content/60" />
                </button>
              </div>

              {!hasCustomDateAccess && (
                <ComingSoon featureKey="advancedGraphs" />
              )}

              <div className="space-y-4">
                {/* Date pickers - always visible but disabled if no access */}
                <div className="new-form-group w-full">
                  <input
                    id="custom-start-date"
                    type="date"
                    className={`form-element ${
                      hasCustomDateAccess
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60"
                    }`}
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || undefined}
                    disabled={!hasCustomDateAccess}
                    style={{
                      colorScheme: "light",
                      backgroundColor: "oklch(var(--b2))",
                    }}
                    onFocus={(e) => {
                      if (hasCustomDateAccess) {
                        try {
                          e.target.showPicker?.();
                        } catch (error) {
                          // Fallback for browsers that don't support showPicker
                        }
                      }
                    }}
                  />
                  <label className="text-base-content/80">Start Date</label>
                </div>

                <div className="new-form-group w-full">
                  <input
                    id="custom-end-date"
                    type="date"
                    className={`form-element ${
                      hasCustomDateAccess
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60"
                    }`}
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate || undefined}
                    disabled={!hasCustomDateAccess}
                    style={{
                      colorScheme: "light",
                      backgroundColor: "oklch(var(--b2))",
                    }}
                    onFocus={(e) => {
                      if (hasCustomDateAccess) {
                        try {
                          e.target.showPicker?.();
                        } catch (error) {
                          // Fallback for browsers that don't support showPicker
                        }
                      }
                    }}
                  />
                  <label className="text-base-content/80">End Date</label>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      await fetchCustomRangeData(
                        customStartDate,
                        customEndDate,
                      );
                      setSelectedTab("Custom");
                      setShowCustomDateModal(false);
                    }}
                    disabled={
                      !hasCustomDateAccess ||
                      !customStartDate ||
                      !customEndDate ||
                      customRangeLoading
                    }
                    className="flex-1 px-4 py-3 bg-primary text-white font-grotesk rounded-xl hover:bg-primary/90 transition-colors disabled:bg-base-300 disabled:text-base-content/60 disabled:cursor-not-allowed"
                  >
                    {customRangeLoading ? "Loading..." : "Load Data"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      {showExportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg max-w-md w-full">
            <div className="p-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-grotesk font-bold text-base-content">
                  Export Data to CSV
                </h3>

                <button
                  onClick={() => setShowExportDialog(false)}
                  className="p-1 rounded-full hover:bg-base-200 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-base-content/60" />
                </button>
              </div>
              {!isPremium && <ComingSoon featureKey="advancedGraphs" />}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowExportDialog(false)}
                  className="px-4 py-2 bg-base-200 text-base-content/80 rounded-md hover:bg-base-300 transition-colors font-medium font-grotesk"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeakInsights;
